import { useState } from 'react';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { errorText, otaBaseline, otaPatch, type OtaValues } from '../lib/device-commands';
import { WEEK_DAYS } from '../lib/ops-schedule';
import {
  TestingConfirm,
  TestingDialogFrame,
  type TestingCommandDialogProps,
} from './TestingDialogFrame';

const clock = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

/**
 * More › OTA Update — port of scio3 `OtaUpdateDialog`. Save = updateDevice
 * `{feature:{otaChannel, otaScheduleRRule, otaReset}}` (allowlisted); "Check update" =
 * executeRemoteCommand 'forceOTA' (confirmed first — the device may download and reboot).
 */
export function TestingOtaDialog({
  open,
  detail,
  deviceName,
  commands,
  onClose,
  onDone,
}: TestingCommandDialogProps) {
  const initial = otaBaseline(detail.feature);
  const [draft, setDraft] = useState<OtaValues>(initial);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [confirmCheck, setConfirmCheck] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = JSON.stringify(otaPatch(draft)) !== JSON.stringify(otaPatch(initial));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await commands.updateDevice(otaPatch(draft));
      toast.success('OTA settings saved');
      onDone();
      onClose();
    } catch (err) {
      const message = errorText(err, 'Failed to save OTA settings');
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const checkUpdate = async () => {
    setConfirmCheck(false);
    setChecking(true);
    try {
      await commands.executeRemoteCommand('forceOTA');
      toast.success('Update check triggered');
      onDone();
    } catch (err) {
      toast.error(errorText(err, 'Failed to trigger update check'));
    } finally {
      setChecking(false);
    }
  };

  const toggleDay = (d: number) =>
    setDraft((v) => {
      const on = v.days.includes(d);
      if (on && v.days.length === 1) return v; // at least one day
      return { ...v, days: on ? v.days.filter((x) => x !== d) : [...v.days, d].sort((a, b) => a - b) };
    });

  return (
    <TestingDialogFrame
      open={open}
      onClose={onClose}
      title={'OTA Update'}
      icon={Download}
      testId="testing-ota-dialog"
      size="lg"
      footer={
        <>
          <Button
            variant="outline"
            className="text-destructive"
            onClick={() => setConfirmCheck(true)}
            disabled={checking || saving}
            data-testid="testing-ota-check"
          >
            {checking ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="h-4 w-4" aria-hidden />
            )}
            {'Check update'}
          </Button>
          <Button
            onClick={() => void save()}
            disabled={!dirty || saving}
            disabledTooltip={!dirty && !saving ? 'No changes to save' : undefined}
            data-testid="testing-ota-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            {'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="testing-ota-channel">{'Channel'}</Label>
        <Select
          value={draft.channel}
          onValueChange={(c) => setDraft((v) => ({ ...v, channel: c === 'beta' ? 'beta' : 'stable' }))}
        >
          <SelectTrigger id="testing-ota-channel" className="cursor-pointer">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="stable">{'Latest Stable'}</SelectItem>
            <SelectItem value="beta">{'Beta Test'}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{'Update window — days'}</Label>
        <div className="flex flex-wrap gap-1.5">
          {WEEK_DAYS.map((d) => {
            const on = draft.days.includes(d.value);
            return (
              <Button
                key={d.value}
                type="button"
                variant={on ? 'default' : 'outline'}
                size="icon-md"
                className="shrink-0 rounded-full text-xs"
                aria-label={d.name}
                aria-pressed={on}
                onClick={() => toggleDay(d.value)}
                data-testid={`testing-ota-day-${d.byDay}`}
              >
                {d.short}
              </Button>
            );
          })}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="testing-ota-time">{'Update window — time (device local)'}</Label>
        <Input
          id="testing-ota-time"
          type="time"
          className="w-36"
          value={clock(draft.hour, draft.minute)}
          onChange={(e) => {
            const [h, m] = e.target.value.split(':').map(Number);
            if (Number.isFinite(h) && Number.isFinite(m)) {
              setDraft((v) => ({ ...v, hour: h ?? 0, minute: m ?? 0 }));
            }
          }}
        />
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id="testing-ota-reset"
          checked={draft.reset}
          onCheckedChange={(c) => setDraft((v) => ({ ...v, reset: c === true }))}
        />
        <Label htmlFor="testing-ota-reset" className="cursor-pointer text-sm font-normal">
          {'Reset after OTA'}
        </Label>
      </div>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      <TestingConfirm
        open={confirmCheck}
        title={'Check for an OTA update'}
        description={`Ask ${deviceName} to check for an OS update now? If one is available it downloads and the device reboots.`}
        confirmLabel={'Check update'}
        onConfirm={() => void checkUpdate()}
        onCancel={() => setConfirmCheck(false)}
      />
    </TestingDialogFrame>
  );
}
